import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutGrid } from "lucide-react";
import { gridSize, gridInstances, clampGrid } from "@/components/bi/ChartGrid";

const AGGS = ["sum", "avg", "count", "count_distinct", "min", "max", "median"];

// Item 10 — Row × Column grid of Pie/Doughnut charts with independent bindings per instance.
export function ChartGridEditor({ config, patchConfig, dims = [], measureFields = [], disp = (x) => x }) {
  const { rows, cols } = gridSize(config);
  const count = rows * cols;
  const instances = gridInstances(config, count);
  const setGrid = (patch) => {
    const next = { rows, cols, ...patch };
    const n = clampGrid(next.rows) * clampGrid(next.cols);
    // keep existing instance bindings; pad/trim to the new cell count
    patchConfig({ chart_grid: { rows: clampGrid(next.rows), cols: clampGrid(next.cols) }, chart_instances: gridInstances(config, n) });
  };
  const setInst = (i, patch) => {
    const list = gridInstances(config, count).map((x, j) => (j === i ? { ...x, ...patch } : x));
    patchConfig({ chart_instances: list });
  };
  return (
    <div data-testid="chart-grid-editor" className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2.5 space-y-2">
      <div className="flex items-center gap-1.5">
        <LayoutGrid className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" />
        <Label className="text-xs text-[color:var(--ink-2)]">Chart grid</Label>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <Label className="text-[10px] text-[color:var(--ink-3)]">Row count</Label>
          <Input data-testid="chart-grid-rows" type="number" min={1} max={4} value={rows}
            onChange={(e) => setGrid({ rows: e.target.value })} className="mt-0.5 h-7 text-xs bg-[color:var(--surface)]" />
        </div>
        <div>
          <Label className="text-[10px] text-[color:var(--ink-3)]">Column count</Label>
          <Input data-testid="chart-grid-cols" type="number" min={1} max={4} value={cols}
            onChange={(e) => setGrid({ cols: e.target.value })} className="mt-0.5 h-7 text-xs bg-[color:var(--surface)]" />
        </div>
      </div>
      {count === 1 ? (
        <p className="text-[10px] text-[color:var(--ink-3)] leading-tight">
          A 1 × 1 grid uses the wells above. Set e.g. 1 × 4 or 2 × 2 to render several independent charts in this widget.
        </p>
      ) : (
        <div className="space-y-2">
          <p className="text-[10px] text-[color:var(--ink-3)] leading-tight">
            {count} charts. Row groups the slices (inner ring), Column is the slice category, Value is the measure — each chart is independent.
          </p>
          {instances.map((inst, i) => (
            <div key={i} data-testid={`chart-grid-inst-${i}`} className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] font-semibold text-[color:var(--brand-ocean)] w-10 shrink-0">#{i + 1}</span>
                <Input data-testid={`chart-grid-inst-title-${i}`} value={inst.title} placeholder="Chart title (optional)"
                  onChange={(e) => setInst(i, { title: e.target.value })} className="h-7 text-xs bg-[color:var(--surface-2)]" />
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <Label className="text-[10px] text-[color:var(--ink-3)]">Row (group)</Label>
                  <Select value={inst.row || "__none"} onValueChange={(v) => setInst(i, { row: v === "__none" ? null : v })}>
                    <SelectTrigger data-testid={`chart-grid-inst-row-${i}`} className="mt-0.5 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">None</SelectItem>
                      {dims.map((c) => <SelectItem key={c.name} value={c.name}>{disp(c.name)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-[color:var(--ink-3)]">Column (slice)</Label>
                  <Select value={inst.column || ""} onValueChange={(v) => setInst(i, { column: v })}>
                    <SelectTrigger data-testid={`chart-grid-inst-col-${i}`} className="mt-0.5 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue placeholder="Pick a field" /></SelectTrigger>
                    <SelectContent>{dims.map((c) => <SelectItem key={c.name} value={c.name}>{disp(c.name)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <Label className="text-[10px] text-[color:var(--ink-3)]">Value</Label>
                  <Select value={inst.value?.field || ""} onValueChange={(v) => setInst(i, { value: { field: v, agg: inst.value?.agg || "sum" } })}>
                    <SelectTrigger data-testid={`chart-grid-inst-val-${i}`} className="mt-0.5 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue placeholder="Measure" /></SelectTrigger>
                    <SelectContent>{measureFields.map((c) => <SelectItem key={c.name} value={c.name}>{disp(c.name)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-[color:var(--ink-3)]">Aggregation</Label>
                  <Select value={inst.value?.agg || "sum"} onValueChange={(v) => setInst(i, { value: { field: inst.value?.field || "", agg: v } })}>
                    <SelectTrigger data-testid={`chart-grid-inst-agg-${i}`} className="mt-0.5 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                    <SelectContent>{AGGS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <Label className="text-[10px] text-[color:var(--ink-3)]">Labels</Label>
                  <Select value={inst.options?.showLabels === false ? "off" : "on"}
                    onValueChange={(v) => setInst(i, { options: { ...(inst.options || {}), showLabels: v === "on" } })}>
                    <SelectTrigger data-testid={`chart-grid-inst-labels-${i}`} className="mt-0.5 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="on">Show</SelectItem><SelectItem value="off">Hide</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-[color:var(--ink-3)]">Legend</Label>
                  <Select value={inst.options?.legendMode === "hide" ? "none" : (inst.options?.legendPos || "none")}
                    onValueChange={(v) => setInst(i, { options: { ...(inst.options || {}), ...(v === "none" ? { legendMode: "hide" } : { legendMode: "show", legendPos: v }) } })}>
                    <SelectTrigger data-testid={`chart-grid-inst-legend-${i}`} className="mt-0.5 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Hidden</SelectItem>
                      <SelectItem value="top">Top</SelectItem>
                      <SelectItem value="bottom">Bottom</SelectItem>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-[color:var(--ink-3)]">Sort slices</Label>
                <Select value={inst.options?.sortBy || "none"}
                  onValueChange={(v) => setInst(i, { options: { ...(inst.options || {}), sortBy: v === "none" ? null : v } })}>
                  <SelectTrigger data-testid={`chart-grid-inst-sort-${i}`} className="mt-0.5 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Source order</SelectItem>
                    <SelectItem value="measure_desc">Value (high → low)</SelectItem>
                    <SelectItem value="measure_asc">Value (low → high)</SelectItem>
                    <SelectItem value="cat_asc">Label (A → Z)</SelectItem>
                    <SelectItem value="cat_desc">Label (Z → A)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
